<script lang="ts" setup>
defineProps<{
  message?: string
}>()
</script>

<template>
  <div class="page-note">
    <div
      v-if="message"
      class="page-note-message"
      v-text="message"
    />
  </div>
</template>

<style scoped lang="scss">
// Anchored to the top with a fractional viewport-height padding so the
// note reads in the upper portion of the screen (above center) rather
// than at the exact vertical middle.
.page-note {
  color: var(--text-dark);
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  // Center within the full-width page itself rather than relying on the
  // parent's flex centering — full-page cards render the note as a later
  // (non-flipping) page, which doesn't provide that centering.
  margin-inline: auto;
  max-width: 768px;
  width: 100%;
}

// Font is sized from the catalogue once and never re-interpolated, so
// the text doesn't reflow / shift as the cover opens. The "grows with
// the cover" visual is done via `transform: scale(--cover-progress)`
// — transforms don't trigger layout, so the text stays in the same
// place and just looks smaller at progress 0 → larger at progress 1.
// Defaults to 1 so the message is full-size on pages where
// --cover-progress isn't being driven.
.page-note-message {
  color: var(--text-dark);

  // Font family / weight / size / line-height all come from the
  // catalogue entry the admin picked (see `findGreetingCardFont`).
  // Each falls back to a sensible default when no font is picked.
  font-family: var(--card-font-family, inherit);
  font-size: calc(16px + var(--cover-progress, 1) * (var(--card-font-size, 22px) - 16px));
  font-weight: var(--card-font-weight, 400);
  line-height: var(--card-line-height, 1.5);

  // Hidden while the cover is opening; fades in after it's fully open.
  // The transition itself is set by ReadingStage via
  // `--cover-fully-open-transition` so we can use a delayed fade for
  // the open direction and an instant snap-to-0 for the close
  // direction. Default keeps the delayed fade if the var isn't set
  // (e.g. when this page is rendered outside the reading stage).
  opacity: var(--cover-fully-open, 1);
  overflow-y: auto;
  padding: 40px 32px calc(var(--nav-bar-bottom) + 45px);
  text-align: center;

  // `touch-action: pan-y` re-stated here because the property doesn't
  // inherit from `.reading-stage` — without it, a touch that lands on
  // this element falls back to `auto` and the browser eats horizontal
  // pans (sniffing for scroll direction), so the cover-close /
  // page-swipe gesture in ReadingStage never sees them on long notes.
  touch-action: pan-y;
  transition: var(--cover-fully-open-transition, opacity 0.4s ease-out 0.3s);
  white-space: pre-wrap;
}
</style>
